import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMagnifyingGlass, faPenToSquare, faUserGroup,
    faComments, faXmark, faShieldHalved, faAddressBook, faPhone, faHourglassHalf
} from '@fortawesome/free-solid-svg-icons';

import api from '../utils/api';
import { getSocket, onSocket, holdConversationRoom } from '../utils/socket';
import ConversationList, { stamp } from '../components/chat/ConversationList';
import ChatWindow from '../components/chat/ChatWindow';
import GroupInfoPanel from '../components/chat/GroupInfoPanel';
import NewGroupModal from '../components/chat/NewGroupModal';
import PeoplePicker from '../components/chat/PeoplePicker';
import ChatContextMenu from '../components/chat/ChatContextMenu';
import CallList from '../components/chat/CallList';
import ChatSearchResults from '../components/chat/ChatSearchResults';
import { useRecentSearches } from '../components/RecentSearches';
import InactiveGroups from '../components/chat/InactiveGroups';
import { useCall } from '../context/CallContext';
import EmployeeAvatar from '../components/EmployeeAvatar';
import '../styles/messenger.css';
import { orgLabel } from '../utils/orgTaxonomy';
import { createChatCache } from '../utils/chatCache';
import { isComposing, onComposingChange } from '../utils/composerGuard';
import { readChatStore, writeChatStore } from '../utils/chatStore';
import { bumpConversation, sidebarPreview, sidebarRows } from '../utils/chatSidebar';
import WithPresence from '../components/PresenceDot';
import ChatTasksPanel from '../components/chat/ChatTasksPanel';
import useChatTasks from '../hooks/useChatTasks';

/**
 * Groups & internal chat — feature draft Module 3.
 *
 * Owns the conversation list and which one is open; ChatWindow owns the
 * messages inside it. The split matters because the list has to keep updating
 * for conversations that are *not* open — that is what the unread badge is —
 * so its socket subscription lives here rather than in the window.
 */

/*
 * The three things the left pane can be showing.
 *
 * A tab rather than another chip, because these are not filters of one list:
 * Calls is a different collection entirely, and mixing "show me fewer chats"
 * with "show me something that is not a chat" in one row of chips leaves no way
 * to tell which of them are mutually exclusive.
 */
const TABS = [
    { key: 'chats', label: 'Chats', icon: faComments },
    { key: 'groups', label: 'Groups', icon: faUserGroup },
    { key: 'calls', label: 'Calls', icon: faPhone },

    /*
     * ADMIN only, and a tab rather than somewhere in Settings because the
     * groups it lists are chat objects an admin will want to open, read and
     * judge before deleting - which means it belongs beside the chats, not in
     * a different part of the app.
     *
     * It is not folded into Oversight: oversight is "every conversation in the
     * company" and answers a question about people, while this is a queue of
     * things about to be destroyed and answers a question about housekeeping.
     * Mixing them would put a delete-everything control on the screen built
     * for reading other people's conversations.
     */
    { key: 'inactive', label: 'Inactive', icon: faHourglassHalf, adminOnly: true }
];

// Chips narrow whatever the active tab is showing. Groups no longer appears
// among them - it is a tab now, and offering it twice invites the two to
// disagree about what "groups" means.
const CHAT_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'direct', label: 'Direct' },
    { key: 'people', label: 'People' }
];

const GROUP_FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'projects', label: 'Projects' }
];

const EMPTY_OUTBOX = [];

const Chats = () => {
    const { id: routeId, token: joinToken } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const me = JSON.parse(localStorage.getItem('user') || '{}');
    const myId = me?._id || me?.id;
    const myRole = me?.role;
    const chatCache = React.useMemo(() => createChatCache(api, { persistFor: myId }), [myId]);
    const [threadVersion, setThreadVersion] = useState(0);

    // The list as it was on the last visit paints at once; loadList replaces it moments later.
    const [conversations, setConversations] = useState(() => readChatStore(myId, 'list') || []);
    // Unsent uploads stay in memory (including their File for Retry), never in localStorage.
    const [outbox, setOutbox] = useState({});
    const recentActivity = useRef(new Map());
    const listRequest = useRef(0);
    const [activeId, setActiveId] = useState(routeId || null);
    const [active, setActive] = useState(null);
    const [loading, setLoading] = useState(() => !conversations.length);

    const [filter, setFilter] = useState('all');
    const [tab, setTab] = useState('chats');

    // Calling back from the Calls tab without opening the thread first.
    const { startCall } = useCall() || {};

    /*
     * Switching tab resets the chip, because the chips are not the same set on
     * both sides - People has no meaning under Groups, Projects none under
     * Chats - and a stale key would silently filter everything away.
     */
    const switchTab = (key) => {
        setTab(key);
        setFilter('all');
        setSearching(false);
        setListQuery('');
    };
    const [listQuery, setListQuery] = useState('');
    // Calls keep their own recent searches; every other tab shares the chat ones.
    const recentSearch = useRecentSearches(tab === 'calls' ? 'calls' : 'chats', {
        value: listQuery,
        onPick: (term) => {
            setListQuery(term);
            if (tab !== 'calls' && tab !== 'inactive') setSearching(true);
        }
    });
    const [searching, setSearching] = useState(false);
    const [jumpTarget, setJumpTarget] = useState(null);

    // The employee directory, shown under the People tab. Loaded lazily: most
    // visits never open that tab, and it is the one list here that can run to
    // hundreds of rows.
    const [contacts, setContacts] = useState([]);
    const [contactsLoaded, setContactsLoaded] = useState(false);

    const [showInfo, setShowInfo] = useState(false);
    // The Tasks panel takes the same place on the right as the details pane,
    // so opening one closes the other.
    const [showTasks, setShowTasks] = useState(false);
    // Direct chats only, and only where you are really in the chat: an
    // oversight admin reading someone else's thread has no tasks "between" them.
    const taskPerson = active?.kind === 'direct' && active.isMember && !active.myHidden
        ? active.otherUser?._id || null
        : null;
    const chatTasks = useChatTasks(taskPerson);
    // Only once the server has said this person is in my scope.
    const canSeeTasks = Boolean(taskPerson) && chatTasks.allowed;
    const toggleTasks = useCallback(() => {
        setShowInfo(false);
        setShowTasks((open) => !open);
    }, []);
    const [showNewGroup, setShowNewGroup] = useState(false);
    const [showNewChat, setShowNewChat] = useState(false);
    const [oversightMode, setOversightMode] = useState(false);
    // { x, y, conversation } while a right-click menu is open on a list row.
    const [ctxMenu, setCtxMenu] = useState(null);

    const activeIdRef = useRef(activeId);
    const activeRequest = useRef(0);
    // conversation id -> release function from holdConversationRoom.
    const joinedRooms = useRef(new Map());
    activeIdRef.current = activeId;
    chatCache.setActive(activeId);
    const roomKey = [...new Set([
        active?._id,
        ...(!oversightMode ? conversations.slice(0, 10).map(c => c._id) : [])
    ].filter(Boolean).map(String))].sort().join(',');

    useEffect(() => {
        const wanted = new Set(roomKey ? roomKey.split(',') : []);
        const held = joinedRooms.current;
        for (const [id, release] of held) {
            if (!wanted.has(id)) { release(); held.delete(id); }
        }
        for (const id of wanted) if (!held.has(id)) held.set(id, holdConversationRoom(id));
    }, [roomKey]);

    useEffect(() => {
        const held = joinedRooms.current;
        return () => {
            for (const release of held.values()) release();
            held.clear();
        };
    }, [chatCache]);

    /* ----------------------------- loading ----------------------------- */

    const loadList = useCallback(async () => {
        const request = ++listRequest.current;
        try {
            // The summary leaves out every group's member list, which the list never shows: the open
            // chat loads its own full record (loadActive). Oversight keeps its own endpoint.
            const res = oversightMode
                ? await api.get('/conversations/oversight')
                : await api.get('/conversations', { params: { view: 'summary' } });
            // A slower earlier GET must not replace a newer refetch after a
            // socket event, reconnect, or return to this browser tab.
            if (request !== listRequest.current) return;
            const rows = [...(res.data || [])];
            for (const [id, preview] of recentActivity.current) {
                const serverRow = rows.find(row => String(row._id) === id);
                if (serverRow && (!serverRow.lastActivityAt || new Date(serverRow.lastActivityAt) < new Date(preview.at))) {
                    const updated = bumpConversation(rows, id, preview);
                    rows.splice(0, rows.length, ...updated);
                }
            }
            setConversations(rows);
            if (!oversightMode) writeChatStore(myId, 'list', rows.slice(0, 50));
        } catch (err) {
            if (request !== listRequest.current) return;
            Swal.fire('Error', err.response?.data?.message || 'Could not load your chats.', 'error');
        } finally {
            if (request === listRequest.current) setLoading(false);
        }
    }, [myId, oversightMode]);

    useEffect(() => { loadList(); }, [loadList]);

    // Socket events are fast, but a long-lived tab can still miss one without
    // an observable reconnect. Reconcile the visible list once a minute.
    useEffect(() => {
        const timer = setInterval(() => {
            if (document.visibilityState === 'visible' && !oversightMode) loadList();
        }, 60_000);
        return () => clearInterval(timer);
    }, [loadList, oversightMode]);

    useEffect(() => {
        let cancelled = false;
        // Oversight reads are audited and should only happen when explicitly opened.
        if (!oversightMode) chatCache.prefetch(conversations.slice(0, 10).map(c => c._id), () => cancelled);
        return () => { cancelled = true; };
    }, [conversations, chatCache, oversightMode]);

    const loadActive = useCallback(async (id, force = false) => {
        const request = ++activeRequest.current;
        if (!id) { setActive(null); return; }
        if (force) chatCache.invalidate(id, { conversation: true });
        const cached = chatCache.peek(id)?.conversation;
        if (String(activeIdRef.current) === String(id)) {
            setActive(cached ? { ...cached, myName: me?.name } : null);
        }
        try {
            const data = await chatCache.loadConversation(id);
            if (request !== activeRequest.current || String(activeIdRef.current) !== String(id)) return;
            setActive({ ...data, myName: me?.name });
        } catch (err) {
            if (request !== activeRequest.current || String(activeIdRef.current) !== String(id)) return;
            chatCache.invalidate(id, { remove: true });
            setActive(null);
            setActiveId(null);
            navigate('/chats', { replace: true });
            Swal.fire('Error', err.response?.data?.message || 'Could not open that chat.', 'error');
        }
        // `me.name` is read from localStorage once at mount and never changes
        // during a session, so it does not belong in the dependency list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatCache, navigate]);

    // The conversation whose rebuild was put off because its message box was in use.
    const postponedThread = useRef(null);

    useEffect(() => {
        const rebuildThread = (id) => {
            postponedThread.current = null;
            setThreadVersion(value => value + 1);
            loadActive(id, true);
        };
        const refresh = () => {
            if (document.visibilityState !== 'visible') return;
            chatCache.invalidateAll();
            loadList();
            const id = activeIdRef.current;
            if (!id) return;
            // Rebuilding the thread rebuilds its message box. Coming back from
            // the phone's camera is a return to this tab too, so doing it now
            // would throw away the photo being attached (or a half-typed
            // message). Wait until the box is empty again.
            if (isComposing(id)) {
                postponedThread.current = id;
                return;
            }
            rebuildThread(id);
        };
        // Not on the very change that emptied the box: a message that has
        // just been sent gets a moment to land first.
        let settle = null;
        const catchUp = () => {
            clearTimeout(settle);
            if (!postponedThread.current) return;
            settle = setTimeout(() => {
                const id = postponedThread.current;
                if (!id) return;
                if (String(activeIdRef.current) !== String(id)) { postponedThread.current = null; return; }
                if (!isComposing(id)) rebuildThread(id);
            }, 800);
        };
        document.addEventListener('visibilitychange', refresh);
        const stopListening = onComposingChange(catchUp);
        return () => {
            document.removeEventListener('visibilitychange', refresh);
            stopListening();
            clearTimeout(settle);
        };
    }, [chatCache, loadList, loadActive]);

    useEffect(() => { loadActive(activeId); }, [activeId, loadActive]);

    // Follow URL changes, not local selection changes: clearing the selected
    // chat must not restore the old route while navigation is still settling.
    useEffect(() => {
        activeIdRef.current = routeId || null;
        setActiveId(routeId || null);
    }, [routeId]);

    /*
     * Arriving with a message to show - the floating voice-note player's "go
     * to this note". Same path as a search hit; the state is cleared so a
     * refresh or a back-and-forward does not jump again.
     */
    useEffect(() => {
        const messageId = location.state?.jumpToMessageId;
        if (!messageId || !routeId) return;
        setJumpTarget({ conversationId: String(routeId), messageId: String(messageId), requestId: Date.now() });
        navigate(location.pathname, { replace: true, state: null });
    }, [location.key, location.pathname, location.state, navigate, routeId]);

    /* --------------------------- join by link --------------------------- */

    useEffect(() => {
        if (!joinToken) return;
        (async () => {
            try {
                const res = await api.post(`/conversations/join/${joinToken}`);
                await loadList();
                navigate(`/chats/${res.data._id}`, { replace: true });
            } catch (err) {
                Swal.fire('Error', err.response?.data?.message || 'That invite link did not work.', 'error');
                navigate('/chats', { replace: true });
            }
        })();
    }, [joinToken, navigate, loadList]);

    /* ------------------------------ realtime ------------------------------ */

    useEffect(() => {
        getSocket();

        // A message in a conversation that is NOT open: bump it to the top of
        // the list and raise its badge. The open one is handled by ChatWindow,
        // which also clears the badge by marking it read.
        const offActivity = onSocket('conversation:activity', (payload) => {
            const cid = String(payload.conversationId);
            const isOpen = String(activeIdRef.current) === cid;
            if (!isOpen) chatCache.invalidate(cid);
            const preview = { ...payload.lastMessage, senderName: payload.senderName, at: payload.at };
            const remembered = recentActivity.current.get(cid);
            if (!remembered || new Date(preview.at) >= new Date(remembered.at)) {
                recentActivity.current.set(cid, preview);
            }

            setConversations((prev) => {
                const idx = prev.findIndex((c) => String(c._id) === cid);
                if (idx === -1) {
                    // A conversation we have never seen — usually a first DM.
                    loadList();
                    return prev;
                }
                if (prev[idx].lastActivityAt && preview.at
                    && new Date(prev[idx].lastActivityAt) > new Date(preview.at)) return prev;
                const next = [...prev];
                const [row] = next.splice(idx, 1);
                next.unshift({
                    ...row,
                    lastMessage: preview,
                    lastActivityAt: payload.at,
                    unread: isOpen ? 0 : (row.unread || 0) + 1
                });
                return next;
            });
        });

        const offNew = onSocket('conversation:new', () => loadList());
        const offUpdated = onSocket('conversation:updated', (payload) => {
            chatCache.invalidate(payload._id, { conversation: true });
            setConversations((prev) => prev.map((c) => (
                String(c._id) === String(payload._id) ? { ...c, ...payload, unread: c.unread } : c
            )));
            if (String(activeIdRef.current) === String(payload._id)) loadActive(payload._id);
        });

        const offRemoved = onSocket('conversation:removed', ({ _id }) => {
            chatCache.invalidate(_id, { remove: true });
            setConversations((prev) => prev.filter((c) => String(c._id) !== String(_id)));
            if (String(activeIdRef.current) === String(_id)) {
                activeIdRef.current = null;
                setActiveId(null);
                setActive(null);
                navigate('/chats', { replace: true });
            }
        });

        /*
         * An admin deleted a group somebody else is sitting in.
         *
         * Distinct from conversation:removed, which means "you are no longer a
         * member" - the group still exists for everyone else. Here there is
         * nothing left to go back to, so anyone with it open is told why their
         * screen just emptied rather than being left looking at a thread whose
         * every action now fails.
         */
        const offDeleted = onSocket('conversation:deleted', ({ conversationId, name }) => {
            chatCache.invalidate(conversationId, { remove: true });
            setConversations((prev) => prev.filter((c) => String(c._id) !== String(conversationId)));
            if (String(activeIdRef.current) === String(conversationId)) {
                activeIdRef.current = null;
                setActiveId(null);
                setActive(null);
                setShowInfo(false);
                navigate('/chats', { replace: true });
                Swal.fire({
                    icon: 'info',
                    title: 'This group was deleted',
                    text: `"${name || 'The group'}" has been removed by an administrator.`,
                    timer: 3200,
                    showConfirmButton: false
                });
            }
        });

        /*
         * A group lapsing, or coming back.
         *
         * Merged into the row rather than refetching the list: the countdown
         * is the only thing that changed, and a full reload would throw away
         * the unread counts the list is holding for every other conversation.
         */
        const offExpiry = onSocket('conversation:expiry', ({ conversationId, expiry, state }) => {
            chatCache.invalidate(conversationId, { conversation: true });
            setConversations((prev) => prev.map((c) => (
                String(c._id) === String(conversationId)
                    ? { ...c, expiry, expiryState: state }
                    : c
            )));
            setActive((prev) => (
                prev && String(prev._id) === String(conversationId)
                    ? { ...prev, expiry, expiryState: state }
                    : prev
            ));
        });

        /*
         * A call in one of our groups started, changed or ended. Merged into
         * the row like the expiry above, so the list can mark it and the open
         * chat can offer "Join" without refetching anything.
         */
        const offOngoing = onSocket('call:ongoing', ({ conversationId, call }) => {
            chatCache.invalidate(conversationId, { conversation: true });
            setConversations((prev) => prev.map((c) => (
                String(c._id) === String(conversationId) ? { ...c, ongoingCall: call } : c
            )));
            setActive((prev) => (
                prev && String(prev._id) === String(conversationId)
                    ? { ...prev, ongoingCall: call }
                    : prev
            ));
        });

        const offReconnect = onSocket('connect', () => {
            chatCache.invalidateAll();
            loadList();
            setThreadVersion(value => value + 1);
            loadActive(activeIdRef.current);
        });
        // Keep the preloaded rooms fresh even when a different chat is open.
        const offChanges = ['message:edited', 'message:deleted', 'message:reaction', 'message:media'].map(event =>
            onSocket(event, payload => {
                if (String(activeIdRef.current) !== String(payload.conversationId) && chatCache.peek(payload.conversationId)) {
                    chatCache.invalidate(payload.conversationId);
                    chatCache.loadMessages(payload.conversationId).catch(() => {});
                }
            }));
        return () => { offActivity(); offNew(); offUpdated(); offRemoved(); offDeleted(); offExpiry(); offOngoing(); offReconnect(); offChanges.forEach(off => off()); };
    }, [loadList, loadActive, chatCache, navigate]);

    useEffect(() => {
        if (filter !== 'people' || contactsLoaded) return;
        api.get('/conversations/contacts')
            .then((res) => { setContacts(res.data || []); setContactsLoaded(true); })
            .catch(() => setContactsLoaded(true));
    }, [filter, contactsLoaded]);

    /* ------------------------------- actions ------------------------------- */

    const openConversation = (c, { preserveJump = false } = {}) => {
        if (!preserveJump) setJumpTarget(null);
        if (String(activeIdRef.current) === String(c._id)) return;
        activeIdRef.current = c._id;
        const cached = chatCache.peek(c._id)?.conversation;
        setActive(cached ? { ...cached, myName: me?.name } : null);
        setActiveId(c._id);
        setShowInfo(false);
        // Keep the list in browser history so the phone's back gesture returns
        // to /chats after opening a conversation.
        navigate(`/chats/${c._id}`);
    };

    const startDirect = async (ids) => {
        try {
            const res = await api.post('/conversations/direct', { userId: ids[0] });
            setShowNewChat(false);
            await loadList();
            openConversation(res.data);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not open that chat.', 'error');
        }
    };

    const openSearchHit = (hit) => {
        setJumpTarget({ conversationId: String(hit.conversationId), messageId: String(hit._id), requestId: Date.now() });
        setSearching(false);
        setListQuery('');
        openConversation({ _id: hit.conversationId }, { preserveJump: true });
    };

    /*
     * `/chats?with=<userId>` opens (or starts) the direct thread with that
     * person — how the birthday notification drops you straight into a wish.
     */
    const [searchParams] = useSearchParams();
    const withUser = searchParams.get('with');
    const withHandled = useRef(null);
    useEffect(() => {
        if (!withUser || withHandled.current === withUser) return;
        withHandled.current = withUser;
        if (String(withUser) === String(myId)) { navigate('/chats', { replace: true }); return; }
        startDirect([withUser]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [withUser]);

    const onGroupCreated = async (group) => {
        setShowNewGroup(false);
        await loadList();
        openConversation(group);
    };

    /**
     * Applied when a child changes something the list also shows. `refetch`
     * means "I changed membership, re-read from the server" — cheaper to do
     * once here than to reconcile a member array in three components.
     */
    const onConversationChanged = useCallback((patch) => {
        if (!patch) return;

        if (patch.delivery) {
            const id = String(patch._id);
            setOutbox(prev => {
                const existing = prev[id] || [];
                const clientId = patch.clientId || patch.message?.clientId;
                let messages;
                if (patch.delivery === 'discarded') {
                    messages = existing.filter(message => message.clientId !== clientId);
                } else if (patch.delivery === 'sent') {
                    messages = existing.map(message => message.clientId === clientId
                        ? { ...patch.message, clientId }
                        : message);
                } else if (patch.delivery === 'progress') {
                    messages = existing.map(message => message.clientId === clientId
                        ? { ...message, uploadProgress: patch.uploadProgress, uploadState: patch.uploadState }
                        : message);
                } else {
                    const index = existing.findIndex(message => message.clientId === clientId);
                    const message = patch.delivery === 'failed'
                        ? { ...(existing[index] || patch.message), pending: false, failed: true, error: patch.error }
                        : { ...patch.message, pending: true, failed: false };
                    messages = index < 0 ? [...existing, message] : existing.map((item, i) => i === index ? message : item);
                }
                // Keep a few confirmed messages for a chat reopened while the POST was in flight.
                return { ...prev, [id]: messages.slice(-10) };
            });
            if (patch.delivery === 'sent' && patch.message) {
                const preview = sidebarPreview(patch.message, myId, me?.name);
                const remembered = recentActivity.current.get(id);
                if (!remembered || new Date(preview.at) >= new Date(remembered.at)) {
                    recentActivity.current.set(id, preview);
                }
                setConversations(prev => bumpConversation(prev, id, preview));
            }
            return;
        }

        if (patch.refetch) {
            loadList();
            loadActive(patch._id || activeIdRef.current, true);
            return;
        }

        if (patch.unread === 0) {
            setConversations((prev) => prev.map((c) => (
                String(c._id) === String(patch._id) ? { ...c, unread: 0 } : c
            )));
            return;
        }

        if (patch.bump) {
            setConversations((prev) => {
                const idx = prev.findIndex((c) => String(c._id) === String(patch._id));
                if (idx === -1) return prev;
                const next = [...prev];
                const [row] = next.splice(idx, 1);
                next.unshift({ ...row, lastActivityAt: new Date().toISOString() });
                return next;
            });
            return;
        }

        // A full conversation document came back (rename, membership change).
        setConversations((prev) => prev.map((c) => (
            String(c._id) === String(patch._id) ? { ...c, ...patch, unread: c.unread } : c
        )));
        if (String(activeIdRef.current) === String(patch._id)) {
            setActive((prev) => ({ ...prev, ...patch }));
        }
    }, [loadList, loadActive, myId, me?.name]);

    /**
     * Close chat — take it out of my list until it next moves. Membership and
     * history are untouched, so it returns by itself on the next message.
     */
    const closeChat = async (c) => {
        try {
            await api.post(`/conversations/${c._id}/close`, { closed: true });
            chatCache.invalidate(c._id, { remove: true });
            setConversations((prev) => prev.filter((x) => String(x._id) !== String(c._id)));
            if (String(activeIdRef.current) === String(c._id)) {
                setActiveId(null);
                setActive(null);
                navigate('/chats', { replace: true });
            }
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not close the chat.', 'error');
        }
    };

    /**
     * Clear chat — hide the history from me alone.
     *
     * The wording is deliberate: people arrive expecting WhatsApp's clear, and
     * they should not have to discover from the result that this one does not
     * remove anything for the other person.
     */
    const clearChat = async (c) => {
        const ok = await Swal.fire({
            title: `Clear this chat?`,
            text: 'The messages will be hidden from your view only. Everyone else keeps their copy, and nothing is removed from the company record.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Clear for me'
        });
        if (!ok.isConfirmed) return;

        try {
            await api.post(`/conversations/${c._id}/clear`);
            chatCache.invalidate(c._id, { remove: true });
            setOutbox(prev => {
                const next = { ...prev };
                delete next[String(c._id)];
                return next;
            });
            recentActivity.current.delete(String(c._id));
            setThreadVersion(value => value + 1);
            setConversations((prev) => prev.map((x) => (
                String(x._id) === String(c._id)
                    ? { ...x, unread: 0, lastMessage: { ...x.lastMessage, text: '' } }
                    : x
            )));
            // Force the open thread to re-read, so it empties immediately
            // rather than showing history the server will no longer serve.
            if (String(activeIdRef.current) === String(c._id)) loadActive(c._id);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not clear the chat.', 'error');
        }
    };

    const toggleMute = async (c) => {
        try {
            const res = await api.put(`/conversations/${c._id}/mute`, { muted: !c.muted });
            setConversations((prev) => prev.map((x) => (
                String(x._id) === String(c._id) ? { ...x, muted: res.data.muted } : x
            )));
        } catch {
            Swal.fire('Error', 'Could not update notifications.', 'error');
        }
    };

    const onLeft = (id) => {
        chatCache.invalidate(id, { remove: true });
        setConversations((prev) => prev.filter((c) => String(c._id) !== String(id)));
        setActiveId(null);
        setActive(null);
        setShowInfo(false);
        navigate('/chats', { replace: true });
    };

    /*
     * The admin who pressed delete.
     *
     * Same outcome as leaving as far as this page is concerned - the row goes,
     * the pane closes - so it shares the handler rather than repeating it. The
     * difference is only that everyone else finds out over the socket.
     */
    const onDeleted = onLeft;

    /* ------------------------------ filtering ------------------------------ */

    /*
     * The DM I already have with each colleague, keyed by their user id.
     *
     * Built from the conversation list that is already loaded rather than
     * fetched again — the People tab needs the same previews the All tab shows,
     * and the data for them is sitting in state.
     */
    const directByUser = React.useMemo(() => {
        const map = new Map();
        for (const c of conversations) {
            if (c.kind !== 'direct') continue;
            const other = c.otherUser?._id
                || c.members?.find((m) => String(m.user?._id || m.user) !== String(myId))?.user?._id;
            if (other) map.set(String(other), c);
        }
        return map;
    }, [conversations, myId]);

    // The search box at the top filters whichever list is on screen, so it
    // narrows the directory under the People tab exactly as it narrows chats.
    const people = contacts.filter((p) => {
        if (!listQuery) return true;
        const q = listQuery.toLowerCase();
        return [p.name, p.employeeId, p.department, p.jobTitle]
            .filter(Boolean)
            .some((f) => f.toLowerCase().includes(q));
    });

    const visible = sidebarRows(conversations, outbox, myId, me?.name).filter((c) => {
        if (listQuery && !searching) {
            const title = (c.title || c.name || '').toLowerCase();
            if (!title.includes(listQuery.toLowerCase())) return false;
        }
        // The tab decides what the list is OF; the chip narrows it.
        if (tab === 'groups' && c.kind !== 'group') return false;

        if (filter === 'unread') return c.unread > 0;
        if (filter === 'projects') return c.groupType === 'project';
        if (filter === 'direct') return c.kind === 'direct';
        // 'people' renders the directory instead of this list, never reaching here.
        return true;
    });

    return (
        <div className={`msgr ${activeId ? 'has-open' : ''}`}>
            {/* ------------------------------ LEFT ------------------------------ */}
            <div className="msgr-sidebar">
                <div className="msgr-sidebar-head">
                    <div className="msgr-sidebar-title">
                        <h3>{oversightMode ? 'All conversations' : 'Chats'}</h3>
                        <div className="msgr-head-actions">
                            {/*
                              * Through to the external users directory
                              * (feature draft Module 2). Shown to the roles
                              * that can actually invite somebody, because for
                              * anyone else it is a door to a 403.
                              */}
                            {['ADMIN', 'HR', 'MANAGER', 'TEAM LEAD'].includes(myRole) && (
                                <button
                                    className="msgr-icon-btn"
                                    onClick={() => navigate('/external-users')}
                                    title="Guests — vendors, clients and consultants"
                                >
                                    <FontAwesomeIcon icon={faAddressBook} />
                                </button>
                            )}
                            {myRole === 'ADMIN' && (
                                <button
                                    className={`msgr-icon-btn ${oversightMode ? 'is-on' : ''}`}
                                    onClick={() => { setOversightMode((o) => !o); setActiveId(null); }}
                                    title="Oversight — every conversation in the company"
                                >
                                    <FontAwesomeIcon icon={faShieldHalved} />
                                </button>
                            )}
                            <button
                                className={`msgr-icon-btn ${searching ? 'is-on' : ''}`}
                                onClick={() => { setSearching((s) => !s); setListQuery(''); setTab('chats'); }}
                                title="Search people, chats, messages and files"
                            >
                                <FontAwesomeIcon icon={searching ? faXmark : faMagnifyingGlass} />
                            </button>
                            <button
                                className="msgr-icon-btn"
                                onClick={() => setShowNewChat(true)}
                                title="New chat"
                            >
                                <FontAwesomeIcon icon={faPenToSquare} />
                            </button>
                            <button
                                className="msgr-icon-btn"
                                onClick={() => setShowNewGroup(true)}
                                title="New group"
                            >
                                <FontAwesomeIcon icon={faUserGroup} />
                            </button>
                        </div>
                    </div>

                    <div className="msgr-search">
                        <FontAwesomeIcon icon={faMagnifyingGlass} className="msgr-search-icon" />
                        <input
                            {...recentSearch.inputProps}
                            placeholder={tab === 'calls' ? 'Search calls' : 'Search people, chats, messages, files…'}
                            maxLength={80}
                            value={listQuery}
                            onChange={(e) => {
                                const value = e.target.value;
                                setListQuery(value);
                                if (tab !== 'calls' && tab !== 'inactive') setSearching(Boolean(value.trim()));
                            }}
                        />
                        {recentSearch.menu}
                    </div>
                </div>

                {/*
                  * Oversight is a view of every conversation in the company,
                  * which is a chat concept - there is no company-wide call log
                  * and a call is private to the people who were on it. So the
                  * tabs step aside while it is on rather than offering a Calls
                  * tab that would quietly mean something different.
                  */}
                {!oversightMode && (
                    <div className="msgr-tabs">
                        {TABS.filter((t) => !t.adminOnly || myRole === 'ADMIN').map((t) => (
                            <button
                                key={t.key}
                                className={`msgr-tab ${tab === t.key ? 'active' : ''}`}
                                onClick={() => switchTab(t.key)}
                            >
                                <FontAwesomeIcon icon={t.icon} />
                                <span>{t.label}</span>
                            </button>
                        ))}
                    </div>
                )}

                {!searching && tab !== 'calls' && tab !== 'inactive' && (
                    <div className="msgr-filters">
                        {(tab === 'groups' ? GROUP_FILTERS : CHAT_FILTERS).map((f) => (
                            <button
                                key={f.key}
                                className={`msgr-chip ${filter === f.key ? 'active' : ''}`}
                                onClick={() => setFilter(f.key)}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                )}

                {tab === 'inactive' ? (
                    /*
                      * Its own collection, like Calls: these are call groups
                      * from all over the company, most of which the admin was
                      * never in, so they are not in `conversations` and cannot
                      * be filtered out of it.
                      */
                    <InactiveGroups
                        query={listQuery}
                        onOpenConversation={(g) => { recentSearch.save(); openConversation({ _id: g._id }); }}
                    />
                ) : tab === 'calls' ? (
                    <CallList
                        myId={myId}
                        query={listQuery}
                        onOpenConversation={(id) => { recentSearch.save(); openConversation({ _id: id }); }}
                        /*
                          * A conversation-shaped object rather than a lookup:
                          * the call log holds calls from conversations that may
                          * not be in the sidebar at all, and startCall only ever
                          * needs the id. `kind` matters because it decides
                          * whether the call window titles itself after a group
                          * or after a person.
                          */
                        onCall={(conversationId, other, meta) => { recentSearch.save(); startCall?.(
                            {
                                _id: conversationId,
                                otherUser: other,
                                title: meta?.title || other?.name,
                                kind: meta?.isGroup ? 'group' : 'direct'
                            },
                            'audio'
                        ); }}
                    />
                ) : searching ? (
                    <div className="msgr-list">
                        <ChatSearchResults query={listQuery}
                            onPerson={person => {
                                recentSearch.save();
                                setSearching(false);
                                setListQuery('');
                                const direct = directByUser.get(String(person._id));
                                if (direct) openConversation(direct);
                                else startDirect([person._id]);
                            }}
                            onChat={chat => { recentSearch.save(); setSearching(false); setListQuery(''); openConversation(chat); }}
                            onMessage={hit => { recentSearch.save(); openSearchHit(hit); }} />
                    </div>
                ) : filter === 'people' ? (
                    /*
                     * The employee directory, in the left pane beside the chats.
                     *
                     * WhatsApp keeps contacts behind a button, but here everyone
                     * in the company is messageable and most of them will never
                     * have an existing thread — so hiding the directory one
                     * level down would make "message a colleague you have not
                     * spoken to" the slowest thing in the app.
                     */
                    <div className="msgr-list">
                        {!contactsLoaded && <div className="msgr-empty">Loading…</div>}
                        {contactsLoaded && !people.length && (
                            <div className="msgr-empty">Nobody matches that.</div>
                        )}
                        {people.map((p) => {
                            /*
                             * A colleague you already have a thread with reads
                             * as a chat row — last message, time, unread badge —
                             * exactly as they do under All. Only someone you
                             * have never messaged falls back to their job title,
                             * because there is no conversation to preview yet.
                             */
                            const dm = directByUser.get(String(p._id));
                            const last = dm?.lastMessage || {};
                            const mine = last.sender && String(last.sender) === String(myId);

                            return (
                                <div
                                    key={p._id}
                                    className={`msgr-row ${dm && dm.unread > 0 ? 'unread' : ''}`}
                                    onClick={() => (dm ? openConversation(dm) : startDirect([p._id]))}
                                >
                                    <WithPresence userId={p._id}>
                                        <EmployeeAvatar person={p} className="msgr-avatar" />
                                    </WithPresence>
                                    <div className="msgr-row-body">
                                        <div className="msgr-row-top">
                                            <span className="msgr-row-name">{p.name}</span>
                                            {last.at && <span className="msgr-row-time">{stamp(last.at)}</span>}
                                        </div>
                                        <div className="msgr-row-bottom">
                                            <span className="msgr-row-preview">
                                                {last.text
                                                    ? `${mine ? 'You: ' : ''}${last.text}`
                                                    : [orgLabel(p.jobTitle) || p.role, orgLabel(p.department)].filter(Boolean).join(' · ')}
                                            </span>
                                            {dm?.unread > 0 && (
                                                <span className="msgr-badge">
                                                    {dm.unread > 99 ? '99+' : dm.unread}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : loading ? (
                    <div className="msgr-empty">Loading…</div>
                ) : (
                    <ConversationList
                        conversations={visible}
                        activeId={activeId}
                        onSelect={openConversation}
                        onContextMenu={(e, c) => setCtxMenu({ x: e.clientX, y: e.clientY, conversation: c })}
                        myId={myId}
                    />
                )}
            </div>

            {/* ------------------------------ RIGHT ------------------------------ */}
            {active ? (
                <ChatWindow
                    key={`${myId}:${active._id}:${threadVersion}`}
                    cache={chatCache}
                    manageRoom={false}
                    conversation={active}
                    myId={myId}
                    myRole={myRole}
                    onBack={() => {
                        activeIdRef.current = null;
                        activeRequest.current += 1;
                        setActiveId(null);
                        setActive(null);
                        setShowInfo(false);
                        setShowTasks(false);
                        navigate('/chats', { replace: true });
                    }}
                    onToggleInfo={() => { setShowTasks(false); setShowInfo((s) => !s); }}
                    taskCount={canSeeTasks ? chatTasks.openCount : null}
                    taskUnread={canSeeTasks ? chatTasks.unreadCount : 0}
                    tasksOpen={showTasks}
                    onToggleTasks={canSeeTasks ? toggleTasks : null}
                    onConversationChanged={onConversationChanged}
                    outboxMessages={outbox[String(active._id)] || EMPTY_OUTBOX}
                    jumpTarget={String(jumpTarget?.conversationId) === String(active._id) ? jumpTarget : null}
                    onJumpHandled={() => setJumpTarget(null)}
                />
            ) : (
                <div className="msgr-main">
                    <div className="msgr-placeholder">
                        <FontAwesomeIcon icon={faComments} />
                        <h3>Your conversations</h3>
                        <p>
                            Pick a chat on the left, or start a new one. Project groups are
                            created automatically and everyone assigned a task on the project
                            joins them.
                        </p>
                    </div>
                </div>
            )}

            {showTasks && canSeeTasks && (
                <ChatTasksPanel
                    key={taskPerson}
                    personId={taskPerson}
                    name={(active.otherUser?.name || active.title || '').split(' ')[0]}
                    myRole={myRole}
                    onClose={() => setShowTasks(false)}
                    onCounts={chatTasks.setCounts}
                />
            )}

            {showInfo && active && (
                <GroupInfoPanel
                    conversation={active}
                    myId={myId}
                    myRole={myRole}
                    onShowTasks={canSeeTasks ? () => { setShowInfo(false); toggleTasks(); } : null}
                    onClose={() => setShowInfo(false)}
                    onChanged={onConversationChanged}
                    onLeft={onLeft}
                    onDeleted={onDeleted}
                />
            )}

            {showNewGroup && (
                <NewGroupModal
                    onClose={() => setShowNewGroup(false)}
                    onCreated={onGroupCreated}
                />
            )}

            {ctxMenu && (
                <ChatContextMenu
                    x={ctxMenu.x}
                    y={ctxMenu.y}
                    conversation={ctxMenu.conversation}
                    onClose={() => setCtxMenu(null)}
                    onCloseChat={closeChat}
                    onClearChat={clearChat}
                    onToggleMute={toggleMute}
                />
            )}

            {showNewChat && (
                <PeoplePicker
                    title="New chat"
                    multi={false}
                    onCancel={() => setShowNewChat(false)}
                    onConfirm={startDirect}
                />
            )}
        </div>
    );
};

export default Chats;
